<template>
  <div class="runner">
    <el-form ref="form" :model="form" :rules="rules">
      <el-form-item label="行业" prop="industryId" required>
        <IndustrySelect v-model="form.industryId" :industry.sync="industry" @change="randomModules(true)"/>
        <p v-if="industry != null" class="keywords">行业关键字：{{industry.keywords}}</p>
      </el-form-item>
      <el-form-item label="模块">
        <RunnerModuleSelect
          v-model="form.moduleIds"
          :industry-id="form.industryId"
          :emit-modules-sync="emitModulesSync"
          :modules.sync="modules"
          :parameter="parameter"
        />
        <el-table :data="modules" stripe>
          <el-table-column label="模块名称" prop="fullName"></el-table-column>
          <el-table-column label="展现形式" prop="viewConfig">
            <template slot-scope="{ row }">
              <el-select v-model="row.viewType">
                <el-option v-for="viewType in row.viewTypes" :value="viewType.code" :label="viewType.name"/>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="字段" prop="fieldConfig" class-name="column-field" min-width="200">
            <el-select slot-scope="{ row }" v-model="row.selectedFields" multiple>
              <el-option v-for="field in getModuleFields(row)" :value="field.variable" :label="field.label"/>
            </el-select>
          </el-table-column>
        </el-table>
      </el-form-item>
      <el-form-item v-for="parameterType in parameterTypes" :key="parameterType.id" :label="parameterType.name">
        <el-select v-model="form[parameterType.variable]"  :multiple="parameterType.getType === 'M'">
          <el-option v-for="param in parameterType.parameters" :key="param.id" :value="param.id" :label="param.name"/>
        </el-select>
      </el-form-item>
      <el-button class="button-generate" type="primary" @click="generate" :disabled="isWorking.generate">{{recordId == null ? '立即生成' : '保存配置'}}</el-button>
    </el-form>
  </div>
</template>

<script>
import IndustrySelect from '../../runner/IndustrySelect'
import RunnerModuleSelect from './RunnerModuleSelect'
import {
  fetchParameterTypeList,
  randomParameters,
  generate,
  randomModules,
  saveGenerateConfig
} from '@/api/runner/runner'

export default {
  name: 'Runner',
  components: { RunnerModuleSelect, IndustrySelect },
  props: {
    data: {},
    // 生成记录ID，用于修改生成记录参数
    recordId: {},
    // 初始化参数
    parameter: {},
    outsideCheck: {
      default () {
        return Promise.resolve()
      }
    }
  },
  data () {
    return {
      isWorking: {
        generate: false
      },
      industry: null,
      // 是否触发模块的同步
      emitModulesSync: true,
      form: {
        industryId: null,
        moduleIds: []
      },
      rules: {
        industryId: [
          { required: true, message: '请选择行业' }
        ]
      },
      parameterTypes: [],
      modules: []
    }
  },
  computed: {
    templateId () {
      return this.data.templateId
    }
  },
  watch: {
    templateId () {
      this.fetchParameterTypeList(true)
    }
  },
  methods: {
    // 根据行业随机生成参数，暴露给外面使用
    random (industry) {
      this.industry = industry
      this.form.industryId = industry.id
      this.randomModules()
    },
    // 生成代码
    generate () {
      if (this.modules.length === 0) {
        this.$tip.warning('请选择模块')
        return
      }
      if (this.parameterTypes.length === 0) {
        this.$tip.warning('请先初始化系统参数')
        return
      }
      this.outsideCheck()
        .then(() => {
          if (this.isWorking.generate) {
            return
          }
          this.$refs.form.validate((pass) => {
            if (!pass) {
              return
            }
            this.isWorking.generate = true
            // 生成配置
            const templateConfigs = []
            for (const type of this.parameterTypes) {
              let selectedParameter = []
              // 单选
              if (type.getType === 'S') {
                const parameter = type.parameters.find(p => p.id === this.form[type.variable])
                selectedParameter.push({
                  id: parameter.id,
                  code: parameter.code,
                  name: parameter.name,
                  value: parameter.value
                })
              }
              // 多选
              if (type.getType === 'M') {
                const ids = this.form[type.variable]
                const parameters = type.parameters.filter(p => ids.findIndex(id => id === p.id) !== -1)
                selectedParameter = selectedParameter.concat(parameters.map(parameter => {
                  return {
                    id: parameter.id,
                    code: parameter.code,
                    name: parameter.name,
                    value: parameter.value
                  }
                }))
              }
              templateConfigs.push({
                typeId: type.id,
                typeVariable: type.variable,
                typeName: type.name,
                multipleSelect: type.getType === 'M',
                selectedParameter
              })
            }
            for (const module of this.modules) {
              module.fields = this.getModuleFields(module).filter(field => module.selectedFields.findIndex(f => f === field.variable) !== -1)
            }
            // 如果存在记录ID，则执行修改参数配置操作
            if (this.recordId != null) {
              saveGenerateConfig({
                ...this.data,
                ...this.form,
                recordId: this.recordId,
                configs: templateConfigs,
                modules: this.modules
              })
                .then(() => {
                  this.$tip.apiSuccess('生成参数修改，请前往生成记录获取代码')
                  setTimeout(() => {
                    this.$router.back()
                  }, 2000)
                })
                .catch(e => {
                  this.$tip.apiFailed(e)
                })
                .finally(() => {
                  this.isWorking.generate = false
                })
              return
            }
            // 生成系统
            generate({
              ...this.data,
              ...this.form,
              configs: templateConfigs,
              modules: this.modules
            })
              .then(() => {
                this.$tip.apiSuccess('生成成功，请前往生成记录获取代码')
                setTimeout(() => {
                  this.$router.push({ path: '/ai/index' })
                }, 2000)
              })
              .catch(e => {
                this.$tip.apiFailed(e)
              })
              .finally(() => {
                this.isWorking.generate = false
              })
          })
        })
    },
    // 随机产生模块
    randomModules (force = false) {
      this.emitModulesSync = false
      if (force === false) {
        // 从参数中获取
        if (this.parameter != null) {
          console.log('this.parameter', this.parameter)
          this.$nextTick(() => {
            this.form.moduleIds = this.parameter.modules.map(item => item.id)
            this.$nextTick(() => {
              this.emitModulesSync = true
            })
          })
          return
        }
        // 从路由中获取
        if (this.$route.query.moduleIds != null && this.$route.query.moduleIds.trim() !== '') {
          this.form.moduleIds = this.$route.query.moduleIds.split(',').map(item => parseInt(item))
          this.$nextTick(() => {
            this.emitModulesSync = true
          })
          return
        }
      }
      // 随机获取
      randomModules(this.form.industryId)
        .then(data => {
          this.modules = []
          this.__getLeafModules(data.modules, this.modules)
          this.form.moduleIds = this.modules.map(module => module.id)
          this.$nextTick(() => {
            this.emitModulesSync = true
          })
        })
        .catch(e => {
          console.log(e)
          this.form.industryId = null
          this.$tip.apiFailed(e)
        })
    },
    // 查询参数列表
    fetchParameterTypeList (force = false) {
      if (this.templateId == null) {
        return
      }
      // 删除form中的模版配置项
      for (const parameterType of this.parameterTypes) {
        delete this.form[parameterType.variable]
      }
      this.parameterTypes = []
      fetchParameterTypeList({
        templateId: this.templateId
      })
        .then(data => {
          this.parameterTypes = data
          if (force === false) {
            // 从参数中获取
            if (this.parameter != null) {
              const generateConfig = {}
              for (const config of this.parameter.configs) {
                if (config.multipleSelect) {
                  generateConfig[config.typeVariable] = config.selectedParameter
                } else {
                  generateConfig[config.typeVariable] = config.selectedParameter[0]
                }
              }
              return generateConfig
            }
          }
          // 接口随机
          return randomParameters(this.templateId)
        })
        .then(data => {
          for (const key in data) {
            const value = data[key]
            // 多选
            if (value instanceof Array) {
              this.$set(this.form, key, value.map(item => item.id))
              continue
            }
            // 单选
            this.$set(this.form, key, value.id)
          }
        })
        .catch()
    },
    // 获取模块字段
    getModuleFields (module) {
      console.log(module.name, JSON.parse(module.fieldConfig))
      return JSON.parse(module.fieldConfig)
    },
    // 获取选中模块的ID集合（只拿叶节点）
    __getLeafModules (modules, leafModules, parentModules) {
      for (const module of modules) {
        if (!module.isDirectory) {
          const parent = parentModules == null ? null : parentModules[parentModules.length - 1]
          leafModules.push({
            ...module,
            parent,
            fullName: parentModules == null ? module.name : (parentModules.map(p => p.name).join('/') + '/' + module.name),
            // 选中的字段
            selectedFields: module.fields.map(field => field.variable)
          })
          continue
        }
        if (module.children != null && module.children.length > 0) {
          let parents = parentModules == null ? null : JSON.parse(JSON.stringify(parentModules))
          if (parents == null) {
            parents = []
          }
          // 拷贝一份模块数据，并删除children，避免json转换因为循环依赖导致错误
          const copyModule = JSON.parse(JSON.stringify(module))
          delete copyModule.children
          copyModule.parent = parents[parents.length - 1]
          parents.push(copyModule)
          this.__getLeafModules(module.children, leafModules, parents)
        }
      }
    }
  },
  mounted () {
    // 如果传入的data中携带了行业，则自动选择目标行业
    if (this.data.industryId != null) {
      this.form.industryId = this.data.industryId
      this.randomModules()
      this.fetchParameterTypeList()
      return
    }
    // 如果参数中携带了行业，则自动选中行业
    if (this.$route.query.industryId != null && this.$route.query.industryId.trim() !== '') {
      this.form.industryId = parseInt(this.$route.query.industryId)
      this.randomModules()
      this.fetchParameterTypeList()
    }
  }
}
</script>

<style scoped lang="scss">
.runner {
  padding: 20px 40px;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
  background-color: #fff;
  .keywords {
    margin: 0;
    font-size: 12px;
    color: #999;
  }
  /deep/ .el-table {
    .column-field {
      .el-select {
        width: 100%;
      }
    }
  }
  /deep/ .el-form {
    .el-form-item {
      display: flex;
      flex-direction: column;
      .el-form-item__label {
        text-align: left;
      }
      .el-form-item__content > * {
        width: 100%;
      }
    }
    .el-input {
      .el-input__inner {
      }
    }
    .button-generate {
      height: 50px;
      width: 100%;
      font-size: 20px;
    }
  }
}
</style>
